import { apiRequest } from "../auth/api";
import type { PublicTaxonomyItem } from "../taxonomy/api";

export type DogWorkflowStatus =
  | "draft"
  | "pending_review"
  | "published"
  | "needs_changes"
  | "disputed"
  | "archived"
  | "rejected";

export interface DogSummary {
  id: string;
  slug: string;
  registeredName: string;
  callName: string | null;
  breed: PublicTaxonomyItem | null;
  sex: "male" | "female" | "unknown";
  dateOfBirth: string | null;
  workflowStatus: DogWorkflowStatus;
  visibility: "public" | "members" | "private";
  version: number;
  updatedAt: string;
  submittedAt: string | null;
  publishedAt: string | null;
}

export interface DogDetail extends DogSummary {
  dateOfDeath: string | null;
  color: PublicTaxonomyItem | null;
  locationCountry: PublicTaxonomyItem | null;
  locationProvince: PublicTaxonomyItem | null;
  registrationOrganization: PublicTaxonomyItem | null;
  registrationNumber: string | null;
  pedigreeNumber: string | null;
  microchipNumber: string | null;
  breederName: string | null;
  kennel: { id: string; name: string; slug: string } | null;
  sire: { id: string; slug: string; registeredName: string; workflowStatus: DogWorkflowStatus } | null;
  dam: { id: string; slug: string; registeredName: string; workflowStatus: DogWorkflowStatus } | null;
  sourceType: "member" | "kennel" | "registry_import" | "moderator" | "legacy";
  sourceNote: string | null;
  biography: string | null;
  owner: { id: string; displayName: string; publicSlug: string | null } | null;
  verified: boolean;
  verifiedAt: string | null;
  reviewNote: string | null;
  createdByUserId: string;
  createdAt: string;
}

export interface DogInput {
  registeredName: string;
  callName?: string | null;
  breedId: string;
  sex: "male" | "female" | "unknown";
  dateOfBirth?: string | null;
  datePrecision?: "day" | "month" | "year" | "unknown";
  dateOfDeath?: string | null;
  colorId?: string | null;
  locationCountryId?: string | null;
  locationProvinceId?: string | null;
  registrationOrganizationId?: string | null;
  registrationNumber?: string | null;
  pedigreeNumber?: string | null;
  microchipNumber?: string | null;
  breederName?: string | null;
  kennelId?: string | null;
  sireDogId?: string | null;
  damDogId?: string | null;
  sourceType?: "member" | "kennel" | "registry_import" | "moderator" | "legacy";
  sourceNote?: string | null;
  biography?: string | null;
  visibility?: "public" | "members" | "private";
}

export interface DuplicateCandidate {
  dog: DogSummary;
  score: number;
  confidence: "possible" | "probable" | "near_certain";
  reasons: Array<{ code: string; points: number; labelVi: string }>;
  recommendedAction: "review" | "ownership_claim" | "edit_request";
}

export interface DuplicateResult {
  decision: "clear" | "review" | "block";
  candidates: DuplicateCandidate[];
  checkedAt: string;
  threshold: { review: number; block: number };
}

export interface DogPage<T = DogSummary> {
  items: T[];
  nextCursor: string | null;
  total: number;
}

export interface ModerationQueueItem extends DogSummary {
  createdBy: { id: string; displayName: string; publicSlug: string | null };
  assignedModeratorUserId: string | null;
  duplicateDecision: "clear" | "review" | "block" | null;
  duplicateCandidates: number;
  pendingEditRequestId: string | null;
}

export interface MyDogListInput {
  status?: DogWorkflowStatus | "all";
  cursor?: string | null;
  limit?: number;
}

function queryString(values: Record<string, string | number | null | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== undefined && value !== "") params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : "";
}

export function listMyDogs(input: MyDogListInput) {
  return apiRequest<DogPage>(`/api/v1/member/dogs${queryString(input)}`);
}

export function getMyDog(id: string) {
  return apiRequest<{ dog: DogDetail }>(`/api/v1/member/dogs/${encodeURIComponent(id)}`);
}

export function createDog(input: DogInput) {
  return apiRequest<{ dog: DogDetail; duplicate: DuplicateResult }>("/api/v1/member/dogs", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function updateDog(id: string, input: Partial<DogInput> & { expectedVersion: number }) {
  return apiRequest<{
    dog: DogDetail;
    editRequestId: string | null;
    duplicate: DuplicateResult | null;
  }>(`/api/v1/member/dogs/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export function checkDogDuplicates(): Promise<DuplicateResult>;
export function checkDogDuplicates(
  input: DogInput & { excludeDogId?: string | null },
): Promise<{ duplicate: DuplicateResult }>;
export function checkDogDuplicates(
  input?: DogInput & { excludeDogId?: string | null },
): Promise<DuplicateResult | { duplicate: DuplicateResult }> {
  if (!input) {
    return Promise.resolve({
      decision: "clear",
      candidates: [],
      checkedAt: new Date().toISOString(),
      threshold: { review: 45, block: 95 },
    });
  }
  return apiRequest<{ duplicate: DuplicateResult }>("/api/v1/member/dogs/duplicates", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function submitDog(id: string, expectedVersion: number) {
  return apiRequest<{ dog: DogDetail; duplicate: DuplicateResult }>(
    `/api/v1/member/dogs/${encodeURIComponent(id)}/submit`,
    { method: "POST", body: JSON.stringify({ expectedVersion }) },
  );
}

export function claimDog(
  id: string,
  input: { claimType?: "owner" | "co_owner" | "breeder" | "custodian"; note?: string },
) {
  return apiRequest<{ claim: { id: string; status: "pending" } }>(
    `/api/v1/member/dogs/${encodeURIComponent(id)}/claim`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function listPublicDogs(input: {
  q?: string;
  breedId?: string | null;
  cursor?: string | null;
  limit?: number;
} = {}) {
  return apiRequest<DogPage>(`/api/v1/dogs${queryString(input)}`);
}

export function getPublicDog(slug: string) {
  return apiRequest<{ dog: DogDetail }>(`/api/v1/dogs/${encodeURIComponent(slug)}`);
}

export function listDogModerationQueue(input: {
  status?: "pending_review" | "disputed" | "all";
  cursor?: string | null;
  limit?: number;
} = {}) {
  return apiRequest<DogPage<ModerationQueueItem>>(
    `/api/v1/moderation/dogs${queryString(input)}`,
  );
}

export function getDogCompare(id: string) {
  return apiRequest<{
    compare: {
      dog: DogDetail;
      latestVersion: {
        version: number;
        eventType: string;
        snapshot: Record<string, unknown>;
        patch: Record<string, unknown>;
        createdAt: string;
      } | null;
      pendingEditRequest: {
        id: string;
        baseVersion: number;
        patch: Record<string, unknown>;
        criticalFields: string[];
        requestedByUserId: string;
        createdAt: string;
      } | null;
      duplicates: DuplicateCandidate[];
    };
  }>(`/api/v1/moderation/dogs/${encodeURIComponent(id)}/compare`);
}

export function moderateDog(
  id: string,
  input: {
    action: "approve" | "request_changes" | "reject" | "archive" | "dispute";
    note?: string | null;
    expectedVersion: number;
  },
) {
  return apiRequest<{ dog: DogDetail }>(
    `/api/v1/moderation/dogs/${encodeURIComponent(id)}/actions`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function reviewDogEditRequest(
  id: string,
  input: { action: "approve" | "reject"; note?: string; expectedDogVersion: number },
) {
  return apiRequest<{ dog: DogDetail }>(
    `/api/v1/moderation/dog-edit-requests/${encodeURIComponent(id)}`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

export function mergeDog(
  duplicateDogId: string,
  input: { canonicalDogId: string; reason: string; expectedVersion: number },
) {
  return apiRequest<{ canonical: DogDetail; archivedDogId: string }>(
    `/api/v1/moderation/dogs/${encodeURIComponent(duplicateDogId)}/merge`,
    { method: "POST", body: JSON.stringify(input) },
  );
}
